import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const SKELETON_ROWS = ["alpha", "bravo", "charlie", "delta"];

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

const truncateText = (value: string, maxLength: number) =>
	value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;

type EquipmentSummary = {
	brand?: string | null;
	model?: string | null;
};

type ClientSummary = {
	name?: string | null;
	isStore?: boolean | null;
};

type ComplaintRepairEntry = {
	id?: string | number | null;
	repairNumber?: string | number | null;
	storeRepairIdentifier?: string | null;
	storeBox?: string | null;
	clientEquipment?: EquipmentSummary | null;
	client?: ClientSummary | null;
	quotation?: number | null;
	problemName?: string | null;
};

type ComplaintRepairsTableProps = {
	repairs: ComplaintRepairEntry[];
	loading?: boolean;
	isAdmin: boolean;
	onView: (repair: ComplaintRepairEntry) => void;
	onEvaluate: (repair: ComplaintRepairEntry) => void;
	onClientClick?: (client: ClientSummary) => void;
};

const buildEquipmentLabel = (equipment?: EquipmentSummary | null) => {
	if (!equipment) {
		return "-";
	}

	const brand = equipment.brand?.trim() ?? "";
	const model = equipment.model?.trim() ?? "";
	const label = `${brand} ${model}`.trim();
	return label.length > 0 ? label : "-";
};

const buildClientLabel = (client?: ClientSummary | null) => {
	if (!client?.name) {
		return "-";
	}

	const name = client.name.trim();
	return client.isStore ? truncateText(name, 20) : name;
};

export function ComplaintRepairsTable({
	repairs,
	loading = false,
	isAdmin,
	onView,
	onEvaluate,
	onClientClick,
}: ComplaintRepairsTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow
					key={`complaint-skeleton-${rowKey}`}
					className="animate-pulse"
				>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-12 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/30" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-32 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={10}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Sem reclamações para avaliar.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[85px]">#</TableHead>
						<TableHead className="w-[120px] text-center">Ficha Loja</TableHead>
						<TableHead className="w-[80px] text-center">Caixa</TableHead>
						<TableHead className="w-[160px]">Equipamento</TableHead>
						<TableHead className="w-[200px]">Cliente</TableHead>
						<TableHead className="w-[140px] text-center">Orçamento</TableHead>
						<TableHead className="w-[200px] text-center">Avaria</TableHead>
						<TableHead className="w-[190px] text-right">Operações</TableHead>
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : repairs.length > 0 ? (
					<TableBody>
						{repairs.map((repair) => {
							const rowKey = repair.id ?? repair.repairNumber ?? "repair-row";
							const clientLabel = buildClientLabel(repair.client);

							return (
								<TableRow key={rowKey}>
									<TableCell>{repair.repairNumber ?? "-"}</TableCell>
									<TableCell className="text-center">
										{repair.storeRepairIdentifier ?? "-"}
									</TableCell>
									<TableCell className="text-center">
										{repair.storeBox ?? "-"}
									</TableCell>
									<TableCell>
										{buildEquipmentLabel(repair.clientEquipment)}
									</TableCell>
									<TableCell>
										{repair.client && onClientClick ? (
											<button
												type="button"
												className="text-primary underline-offset-2 hover:underline"
												onClick={() => onClientClick(repair.client ?? {})}
											>
												{clientLabel}
											</button>
										) : (
											clientLabel
										)}
									</TableCell>
									<TableCell className="text-center">
										{formatCurrency(normalizeNumber(repair.quotation))}
									</TableCell>
									<TableCell className="text-center">
										{repair.problemName ?? "-"}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												variant="secondary"
												size="sm"
												onClick={() => onView(repair)}
											>
												Ver
											</Button>
											{isAdmin ? (
												<Button
													size="sm"
													className="bg-emerald-600 text-white hover:bg-emerald-500"
													onClick={() => onEvaluate(repair)}
												>
													Avaliar
												</Button>
											) : null}
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				) : (
					renderEmpty()
				)}
			</Table>
		</div>
	);
}
